import { useState } from 'react';
import { formatearCLP } from '@lexicore/shared';
import { IconoCerrar, IconoMas } from '../../componentes/Iconos';
import { BarraPresupuesto, Vacio } from '../../componentes/UI';
import { useAvisos } from '../../contextos/AvisosContext';
import { useCategoriasGasto, type GastoProyecto, type ProyectoDetalle } from '../../lib/consultas';
import {
  useActualizarGasto,
  useCrearGastoEnProyecto,
  useEliminarGasto,
} from '../../lib/mutaciones';

/**
 * Gastos del proyecto: alta, edición y borrado sin salir de la ficha.
 *
 * Es la razón principal de esta pantalla. Antes había que ir a Costos, buscar
 * el proyecto en un desplegable y volver — todo para registrar un gasto cuyo
 * contexto ya se tenía delante.
 */
export function PestanaGastos({ proyecto }: { proyecto: ProyectoDetalle }) {
  const [editando, setEditando] = useState<GastoProyecto | null>(null);
  const [creando, setCreando] = useState(false);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState<GastoProyecto | null>(null);

  const avisos = useAvisos();
  const eliminar = useEliminarGasto();

  const total = proyecto.gastos.reduce((suma, gasto) => suma + gasto.montoNeto, 0);
  const referencia = proyecto.presupuestoCostoPlanificado || proyecto.montoContratoNeto;
  const consumo = referencia > 0 ? (total / referencia) * 100 : 0;

  const borrar = async (gasto: GastoProyecto) => {
    try {
      await eliminar.mutateAsync(gasto.id);
      avisos.exito('Gasto eliminado');
      setConfirmandoBorrado(null);
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo eliminar el gasto.');
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            {proyecto.gastos.length} gasto(s) ·{' '}
            <span className="td-mono" style={{ color: 'var(--text-primary)', fontSize: 14 }}>
              {formatearCLP(total)}
            </span>{' '}
            neto
          </span>
          {proyecto.presupuestoCostoPlanificado > 0 && (
            <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              de {formatearCLP(proyecto.presupuestoCostoPlanificado)} presupuestados
            </span>
          )}
        </div>

        <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreando(true)}>
          <IconoMas size={13} />
          Registrar gasto
        </button>
      </div>

      {referencia > 0 && (
        <div style={{ marginBottom: 20 }}>
          <BarraPresupuesto porcentaje={consumo} />
        </div>
      )}

      <div className="tabla-wrap">
        <div className="tabla-scroll">
          <table style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Categoría</th>
                <th>Concepto</th>
                <th>Proveedor</th>
                <th>Documento</th>
                <th style={{ textAlign: 'right' }}>Neto</th>
                <th>Pago</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {proyecto.gastos.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <Vacio
                      titulo="Este proyecto todavía no tiene gastos"
                      detalle="Sin costos cargados, el margen que muestra la ficha es el ingreso completo — y eso nunca es cierto. Registrar los gastos también alimenta el piso de precio del motor de predicción para las próximas ofertas."
                      accion={
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreando(true)}>
                          <IconoMas size={13} />
                          Registrar el primero
                        </button>
                      }
                    />
                  </td>
                </tr>
              )}

              {proyecto.gastos.map((gasto) => (
                <tr key={gasto.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>{gasto.fecha.slice(0, 10)}</td>
                  <td>
                    <span className="badge badge-cat" style={{ background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)' }}>
                      {gasto.categoriaGasto.nombre}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-primary)' }}>{gasto.concepto}</td>
                  <td>{gasto.proveedorNombre || gasto.proveedor?.nombre || '—'}</td>
                  <td style={{ fontSize: 11 }}>
                    {gasto.numeroDocumento ? (
                      <>
                        <div style={{ fontFamily: 'var(--font-mono)' }}>{gasto.numeroDocumento}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{gasto.tipoDocumento}</div>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="td-mono" style={{ color: 'var(--text-primary)' }}>{formatearCLP(gasto.montoNeto)}</td>
                  <td>
                    <span className={`factura-badge ${gasto.estadoPago === 'PAGADO' ? 'PAGADA' : 'PENDIENTE'}`}>
                      {gasto.estadoPago}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditando(gasto)}>
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setConfirmandoBorrado(gasto)}
                        aria-label={`Eliminar el gasto ${gasto.concepto}`}
                        style={{ color: 'var(--accent-red)' }}
                      >
                        <IconoCerrar size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(creando || editando) && (
        <DialogoGastoProyecto
          proyecto={proyecto}
          gasto={editando}
          alCerrar={() => {
            setCreando(false);
            setEditando(null);
          }}
        />
      )}

      {confirmandoBorrado && (
        <div
          className="modal-overlay"
          onClick={(evento) => evento.target === evento.currentTarget && setConfirmandoBorrado(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h2>Eliminar gasto</h2>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                Se eliminará <strong style={{ color: 'var(--text-primary)' }}>{confirmandoBorrado.concepto}</strong> por{' '}
                <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                  {formatearCLP(confirmandoBorrado.montoNeto)}
                </strong>
                . El margen del proyecto y los indicadores del tablero se recalculan.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmandoBorrado(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void borrar(confirmandoBorrado)}
                disabled={eliminar.isPending}
              >
                {eliminar.isPending ? 'Eliminando…' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DialogoGastoProyecto({
  proyecto,
  gasto,
  alCerrar,
}: {
  proyecto: ProyectoDetalle;
  gasto: GastoProyecto | null;
  alCerrar: () => void;
}) {
  const avisos = useAvisos();
  const crear = useCrearGastoEnProyecto();
  const actualizar = useActualizarGasto();
  const categorias = useCategoriasGasto();

  const [categoriaGastoId, setCategoriaGastoId] = useState(gasto?.categoriaGastoId ?? '');
  const [proveedorNombre, setProveedorNombre] = useState(gasto?.proveedorNombre ?? gasto?.proveedor?.nombre ?? '');
  const [fecha, setFecha] = useState(gasto?.fecha.slice(0, 10) ?? new Date().toISOString().slice(0, 10));
  const [concepto, setConcepto] = useState(gasto?.concepto ?? '');
  const [montoNeto, setMontoNeto] = useState(gasto?.montoNeto ?? 0);
  const [tipoDocumento, setTipoDocumento] = useState(gasto?.tipoDocumento ?? 'FACTURA');
  const [numeroDocumento, setNumeroDocumento] = useState(gasto?.numeroDocumento ?? '');
  const [estadoPago, setEstadoPago] = useState(gasto?.estadoPago ?? 'PENDIENTE');
  const [fechaPago, setFechaPago] = useState(gasto?.fechaPago?.slice(0, 10) ?? '');

  const listo = concepto.trim().length > 0 && montoNeto > 0 && categoriaGastoId !== '';
  const guardando = crear.isPending || actualizar.isPending;

  const guardar = async () => {
    const datos = {
      categoriaGastoId,
      proveedorId: null,
      proveedorNombre: proveedorNombre.trim() || null,
      fecha,
      concepto: concepto.trim(),
      montoNeto,
      tipoDocumento,
      numeroDocumento: numeroDocumento.trim() || undefined,
      estadoPago,
      // El backend exige fecha de pago cuando el gasto se marca como pagado.
      fechaPago: estadoPago === 'PAGADO' ? fechaPago || fecha : null,
    };

    try {
      if (gasto) {
        await actualizar.mutateAsync({ id: gasto.id, ...datos });
        avisos.exito('Gasto actualizado');
      } else {
        await crear.mutateAsync({ ...datos, proyectoId: proyecto.id });
        avisos.exito('Gasto registrado');
      }
      alCerrar();
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo guardar el gasto.');
    }
  };

  return (
    <div className="modal-overlay" onClick={(evento) => evento.target === evento.currentTarget && alCerrar()} role="dialog" aria-modal="true">
      <div className="modal" style={{ maxWidth: 660 }}>
        <div className="modal-header">
          <h2>{gasto ? 'Editar gasto' : 'Registrar gasto'}</h2>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar">
            <IconoCerrar size={14} />
          </button>
        </div>

        <div className="modal-body">
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 18 }}>
            {proyecto.nombre}
          </p>

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="categoria">Categoría</label>
              <select id="categoria" value={categoriaGastoId} onChange={(e) => setCategoriaGastoId(e.target.value)}>
                <option value="">Selecciona una categoría…</option>
                {categorias.data?.map((categoria) => (
                  <option key={categoria.id} value={categoria.id}>
                    {categoria.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="fecha">Fecha</label>
              <input id="fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </div>

            <div className="form-group ancho-total">
              <label htmlFor="concepto">Concepto</label>
              <input
                id="concepto"
                value={concepto}
                onChange={(e) => setConcepto(e.target.value)}
                placeholder="Ej: subcontrato de levantamiento en terreno"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="monto">Monto neto</label>
              <input id="monto" type="number" value={montoNeto || ''} onChange={(e) => setMontoNeto(Number(e.target.value))} min={0} />
              <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                El IVA de compra es recuperable: el costo real del proyecto es el neto.
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="proveedor">Proveedor</label>
              <input
                id="proveedor"
                type="text"
                value={proveedorNombre}
                onChange={(e) => setProveedorNombre(e.target.value)}
                placeholder="Nombre del proveedor (ej: Sodimac, AWS, Freelance)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="tipoDoc">Documento</label>
              <select id="tipoDoc" value={tipoDocumento} onChange={(e) => setTipoDocumento(e.target.value)}>
                <option value="FACTURA">Factura</option>
                <option value="BOLETA">Boleta</option>
                <option value="BOLETA_HONORARIOS">Boleta de honorarios</option>
                <option value="RECIBO">Recibo</option>
                <option value="SIN_DOCUMENTO">Sin documento</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="numeroDoc">N° de documento</label>
              <input id="numeroDoc" value={numeroDocumento} onChange={(e) => setNumeroDocumento(e.target.value)} placeholder="Opcional" />
            </div>

            <div className="form-group">
              <label htmlFor="pago">Estado de pago</label>
              <select id="pago" value={estadoPago} onChange={(e) => setEstadoPago(e.target.value)}>
                <option value="PENDIENTE">Pendiente</option>
                <option value="PAGADO">Pagado</option>
              </select>
            </div>

            {estadoPago === 'PAGADO' && (
              <div className="form-group">
                <label htmlFor="fechaPago">Fecha de pago</label>
                <input id="fechaPago" type="date" value={fechaPago || fecha} onChange={(e) => setFechaPago(e.target.value)} />
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={alCerrar}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void guardar()} disabled={!listo || guardando}>
            {guardando ? 'Guardando…' : gasto ? 'Guardar cambios' : 'Registrar gasto'}
          </button>
        </div>
      </div>
    </div>
  );
}
